// convert between radians and degrees
export const d2r = (d: number): number => d * (Math.PI / 180);
export const r2d = (r: number): number => r / (Math.PI / 180);

/**
 * Find the angle that will produce an arc of a given length at a given radius.
 * Using this to allow for gaps between the segments. Returns angle in radians
 * arcLength = radius * angleInRadians
 *
 * @param {number} arcLength - the sought arc length in local coordinate space
 * @param {number} atRadius - the radius of the arc
 * @returns {number} - the angle in radians of an arc of the given length at the given radius
 */
export const angleForArcLength = (
  arcLength: number,
  atRadius: number
): number => arcLength / atRadius;

/**
 * The viewBox size. Coordinates are computed within this coordinate space
 */
export const size = 100;

/**
 * The center of the viewBox, center of the chart
 */
export const center = size / 2;

/**
 * The diameter of the chart's inner hole in local coordinate space units
 */
export const hole = 62;

/**
 * The thickness of the chart segments for the given size and hole
 */
export const thickness = (size - hole) / 2;

/**
 * The outer radius of the chart
 */
export const radiusOuter = size / 2;

/**
 * The inner radius of the chart
 */
export const radiusInner = radiusOuter - thickness;

/**
 * The size of the gap between chart segments, in local coordinate space units
 */
export const gapSize = 1;

/**
 * Compute the angle offset required to establish the gaps between segments at the inner edge
 */
export const gapAngleOffsetInner = r2d(angleForArcLength(gapSize, radiusInner));

/**
 * Compute the angle offset required to establish the gaps between segments at the outer edge
 */
export const gapAngleOffsetOuter = r2d(angleForArcLength(gapSize, radiusOuter));

/**
 * The minimum angle that won't be swallowed by the gap offsets at the inner edge.
 * Used to compute the minimum value that won't get swallowed (minimumValue defined below)
 */
export const minimumAngleDeg = r2d(angleForArcLength(gapSize * 2, radiusInner));

/**
 * The minimum value that won't get swallowed by the gap offsets at the inner edge
 */
export const minimumValue = minimumAngleDeg / 360;

/**
 * Computes an x/y coordinate for the given angle and radius
 * @param {number} deg - The angle in degrees
 * @param {number} r  - The radius
 * @returns {Array} - An x/y coordinate for the point at the given angle and radius
 */
export const coords = (deg: number, r: number): number[] => {
  const rad = d2r(deg);

  return [center - Math.cos(rad) * r, center - Math.sin(rad) * r];
};
